import { useId, useMemo, useState } from "react";
import {
  MAX_DIRECTORY_KEYWORDS,
  MAX_DIRECTORY_KEYWORD_LENGTH,
  normalizeDirectoryKeyword,
  sanitizeDirectoryKeywords,
} from "@vimos/api-client";

/**
 * Editor de palabras clave de busqueda de un negocio. Opcionales, maximo 3.
 * El propietario puede anadir, editar y eliminar. Se descartan vacias y
 * duplicadas (ignorando mayusculas y tildes). Las sugerencias son palabras
 * clave reales ya usadas por otros negocios (nunca se inventan).
 */
export interface KeywordsInputProps {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  /** Palabras clave sugeridas (reales). Se filtran por el texto escrito. */
  suggestions?: readonly string[];
  max?: number | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

const MAX_SUGGESTIONS = 6;

export function KeywordsInput({
  label,
  value,
  onChange,
  suggestions = [],
  max = MAX_DIRECTORY_KEYWORDS,
  error,
  hint,
  disabled,
  id,
  className = "",
}: KeywordsInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const [draft, setDraft] = useState("");

  const selectedNormalized = useMemo(() => new Set(value.map(normalizeDirectoryKeyword)), [value]);

  const visibleSuggestions = useMemo(() => {
    const query = normalizeDirectoryKeyword(draft);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const suggestion of suggestions) {
      const normalized = normalizeDirectoryKeyword(suggestion);
      if (!normalized || seen.has(normalized) || selectedNormalized.has(normalized)) continue;
      if (query && !normalized.includes(query)) continue;
      seen.add(normalized);
      result.push(suggestion.trim());
      if (result.length >= MAX_SUGGESTIONS) break;
    }
    return result;
  }, [draft, suggestions, selectedNormalized]);

  const atMax = value.length >= max;

  function commit(raw: string) {
    if (disabled || atMax) return;
    const next = sanitizeDirectoryKeywords([...value, raw]);
    setDraft("");
    if (next.length !== value.length) onChange(next);
  }

  function remove(keyword: string) {
    if (disabled) return;
    onChange(value.filter((item) => item !== keyword));
  }

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={inputId} className="font-body text-ink text-sm font-medium">
        {label}
      </label>

      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {value.map((keyword) => (
            <li
              key={keyword}
              className="border-sand/70 bg-paper text-ink flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm"
            >
              <span>{keyword}</span>
              <button
                type="button"
                aria-label={`Quitar la palabra clave ${keyword}`}
                disabled={disabled}
                className="text-mist hover:text-ink focus-visible:ring-gold rounded-full text-base leading-none focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => remove(keyword)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex gap-2">
        <input
          id={inputId}
          type="text"
          value={draft}
          disabled={disabled || atMax}
          maxLength={MAX_DIRECTORY_KEYWORD_LENGTH}
          placeholder={atMax ? `Maximo ${max} palabras clave` : "Escribe una palabra clave"}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
          className={`border-sand/70 bg-paper text-ink placeholder:text-mist focus-visible:ring-gold focus-visible:ring-offset-paper h-11 flex-1 rounded-md border px-3.5 text-base transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${error ? "border-red-500" : "hover:border-ink/25"}`}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit(draft);
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
        />
        <button
          type="button"
          disabled={disabled || atMax || !draft.trim()}
          className="border-ink/15 text-ink hover:border-ink/30 hover:bg-ink/5 focus-visible:ring-gold h-11 rounded-md border px-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => commit(draft)}
        >
          Anadir
        </button>
      </div>

      {visibleSuggestions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {visibleSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={disabled || atMax}
              className="text-mist hover:text-ink border-ink/10 hover:border-ink/30 rounded-full border px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => commit(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      {hint && !error ? (
        <p id={hintId} className="text-mist text-sm">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
